import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AssignmentDetail,
  AssignmentEventHistory,
  AssignmentNote,
  AssignmentNote as AssignmentNoteType,
  AssignmentsService,
  DocumentsService,
  PaginatedAssignmentNoteList,
  PatchedAssignmentNote,
  PatchedAssignmentStatusUpdate,
  SourceTypeEnum,
  Status,
} from '../../../../../js/api';
import { useCurrencyFormatter } from '../../../../../js/app/hooks/use-currency-formatter';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { ActivityLog } from '../../../components/activity-log';
import { DocumentsTab } from '../../../components/documents-tab';
import { DrawerCloseButton } from '../../../components/drawer';
import { DropdownSelect } from '../../../components/dropdown-select';
import { SectionHeader } from '../../../components/SectionHeader';
import { StatusTransitionModal } from '../../../components/status-transition-modal';
import { StatusPill } from '../../../components/StatusPill';
import { Tabs, TabsBottomBar, TabsButton, TabsContent } from '../../../components/tabs';
import { TooltippedText } from '../../../components/tooltipped-text';
import { MENU_BADGES_QUERY_KEY, QUEUE_NOTIFICATIONS_QUERY_KEY } from '../../../constants/queryKeys';
import { StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import { useCollapseOnOverflow } from '../../../hooks/useCollapseOnOverflow';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { StatusMetadata, useWorkflowMetadata } from '../../../hooks/useWorkflowMetadata';
import type {
  ActivityEventHistory,
  ActivityHistory,
  ActivityStatusHistory,
} from '../../../utils/activity-types';
import { SOURCE_TYPE_ORGANIZATION_BADGE_STYLE } from '../../../utils/dataStyles';
import { formatApiDate } from '../../../utils/date';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { READ_ONLY_LABEL_STYLE, READ_ONLY_VALUE_STYLE } from '../../../utils/globalStyles';
import { getStatusTextStyle } from '../../../utils/statusStyles';
import { StatusTransitionButtons } from '../../../utils/statusTransitions';
import { getQueueNameForStatus } from '../../../utils/workflowUtils';
import { ExpensesTab } from './ExpensesTab';
import {
  ASSIGNMENT_CHANGE_HISTORY_QUERY_KEY,
  ASSIGNMENT_EVENT_HISTORY_QUERY_KEY,
  ASSIGNMENT_DOCUMENTS_COUNT_QUERY_KEY,
  ASSIGNMENT_EXPENSES_QUERY_KEY,
  ASSIGNMENT_NOTES_QUERY_KEY,
  ASSIGNMENT_QUERY_KEY,
  ASSIGNMENT_STATUS_HISTORY_QUERY_KEY,
  ASSIGNMENT_TIMECARDS_QUERY_KEY,
  ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY,
  ASSIGNMENT_EXPENSE_CHANGE_HISTORY_QUERY_KEY,
} from './queryKeys';
import { ShiftsTab } from './ShiftsTab';

export type AssignmentDrawerProps = {
  assignmentId: string;
  onClose?: () => void;
  prevId?: string | null;
  nextId?: string | null;
  selectedQueueCode?: string | undefined;
  /** Hide the popout button (e.g. when already on the full-page route) */
  isFullPage?: boolean;
};

/* eslint-disable sonarjs/cognitive-complexity */
export const AssignmentDrawer: React.FC<AssignmentDrawerProps> = ({
  assignmentId,
  onClose,
  prevId,
  nextId,
  selectedQueueCode,
  isFullPage = false,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const drawerRef = useRef<HTMLDivElement>(null);
  const { formatCurrencyPostfix } = useCurrencyFormatter({ postfix: '', placeholder: '-' });

  const defaultTab = 'shifts';
  const validTabs = ['shifts', 'expenses', 'documents'];
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTabState] = useState(
    tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : defaultTab
  );

  const setActiveTab = useCallback(
    (tab: string) => {
      setActiveTabState(tab);
      const next = new URLSearchParams(searchParams);
      if (tab && tab !== defaultTab) {
        next.set('tab', tab);
      } else {
        next.delete('tab');
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [targetStatusCode, setTargetStatusCode] = useState<string | null>(null);
  const [hasShiftDirty, setHasShiftDirty] = useState(false);

  const { statuses: workflowStatuses, isLoading: isLoadingWorkflow } = useWorkflowMetadata(
    WorkflowScopes.Assignment
  );

  // Fetch assignment detail
  const {
    data: assignment,
    isLoading: isLoadingAssignment,
    error: assignmentError,
  } = useQuery<AssignmentDetail>({
    queryKey: [ASSIGNMENT_QUERY_KEY, assignmentId],
    queryFn: () => AssignmentsService.assignmentsAssignmentRetrieve({ id: assignmentId }),
    enabled: !!assignmentId,
    retry: false,
  });

  useDocumentTitle(assignment?.code ?? '', false, isFullPage && !!assignment?.code);

  // Fetch notes
  const { data: notesData } = useQuery({
    queryKey: [ASSIGNMENT_NOTES_QUERY_KEY, assignmentId],
    queryFn: () =>
      AssignmentsService.assignmentsAssignmentNotesList({
        id: assignmentId!,
        ordering: '-created_at',
      }),
    enabled: !!assignmentId,
    select: (data: PaginatedAssignmentNoteList | AssignmentNote | AssignmentNote[]) => {
      if (!data) {
        return [];
      }
      if (Array.isArray(data)) {
        return data;
      }
      if ('results' in data) {
        return data.results || [];
      }
      return [data as AssignmentNote];
    },
  });

  // Fetch status history
  const { data: statusHistory } = useQuery({
    queryKey: [ASSIGNMENT_STATUS_HISTORY_QUERY_KEY, assignmentId],
    queryFn: () =>
      AssignmentsService.assignmentsAssignmentStatusHistoryRetrieve({
        id: assignmentId,
        sort: 'desc',
      }),
    enabled: !!assignmentId,
  });

  // Fetch change history
  const { data: changeHistory } = useQuery({
    queryKey: [ASSIGNMENT_CHANGE_HISTORY_QUERY_KEY, assignmentId],
    queryFn: () =>
      AssignmentsService.assignmentsAssignmentChangeHistoryRetrieve({
        id: assignmentId!,
        sort: 'desc',
      }),
    enabled: !!assignmentId,
  });

  // Fetch event history
  const { data: eventHistoryData } = useQuery({
    queryKey: [ASSIGNMENT_EVENT_HISTORY_QUERY_KEY, assignmentId],
    queryFn: () =>
      AssignmentsService.assignmentsAssignmentEventHistoryRetrieve({
        id: assignmentId!,
        sort: 'desc',
      }),
    enabled: !!assignmentId,
    select: (data: AssignmentEventHistory | AssignmentEventHistory[]) => {
      if (!data) {
        return [];
      }
      if (Array.isArray(data)) {
        return data;
      }
      return [data];
    },
  });

  // Tab badge counts
  const { data: expensesData } = useQuery({
    queryKey: [ASSIGNMENT_EXPENSES_QUERY_KEY, assignmentId],
    queryFn: () => AssignmentsService.assignmentsAssignmentExpensesList({ id: assignmentId }),
    enabled: !!assignmentId,
  });
  const expenseCount = Array.isArray(expensesData) ? expensesData.length : 0;

  const { data: documentsData } = useQuery({
    queryKey: [ASSIGNMENT_DOCUMENTS_COUNT_QUERY_KEY, assignmentId],
    queryFn: () =>
      DocumentsService.documentsList({
        contextType: 'assignments.assignment',
        contextId: assignmentId,
        limit: 1,
      }),
    enabled: !!assignmentId,
  });
  const documentCount = (documentsData as any)?.count ?? 0;

  // Fetch timecards linked to this assignment (for the metadata cross-link)
  const { data: timecardsData } = useQuery({
    queryKey: [ASSIGNMENT_TIMECARDS_QUERY_KEY, assignmentId],
    queryFn: () =>
      AssignmentsService.assignmentsTimecardList({
        assignment: assignmentId,
        ordering: '-created_at',
      }),
    enabled: !!assignmentId,
  });
  const relatedTimecards = timecardsData?.results ?? [];

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_QUERY_KEY, assignmentId] });
    queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_NOTES_QUERY_KEY, assignmentId] });
    queryClient.invalidateQueries({
      queryKey: [ASSIGNMENT_STATUS_HISTORY_QUERY_KEY, assignmentId],
    });
    queryClient.invalidateQueries({
      queryKey: [ASSIGNMENT_CHANGE_HISTORY_QUERY_KEY, assignmentId],
    });
    queryClient.invalidateQueries({
      queryKey: [ASSIGNMENT_EXPENSE_CHANGE_HISTORY_QUERY_KEY, assignmentId],
    });
    queryClient.invalidateQueries({
      queryKey: [ASSIGNMENT_EVENT_HISTORY_QUERY_KEY, assignmentId],
    });
    queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_EXPENSES_QUERY_KEY, assignmentId] });
    queryClient.invalidateQueries({
      queryKey: [ASSIGNMENT_DOCUMENTS_COUNT_QUERY_KEY, assignmentId],
    });
    queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_TIMECARDS_QUERY_KEY, assignmentId] });
    queryClient.invalidateQueries({ queryKey: [ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY] });
  }, [queryClient, assignmentId]);

  // Navigate to next/prev after item leaves queue
  const checkAndNavigateIfRemoved = useCallback(
    (updated?: AssignmentDetail) => {
      if (!selectedQueueCode || selectedQueueCode === 'all') return;
      const curStatusCode = updated?.current_status?.code ?? assignment?.current_status?.code;
      const queueName = getQueueNameForStatus(workflowStatuses, curStatusCode);

      if (queueName && queueName !== selectedQueueCode) {
        const targetId = nextId || prevId || null;
        const nextParams = new URLSearchParams(searchParams);
        if (targetId) {
          nextParams.set('id', String(targetId));
        } else {
          nextParams.delete('id');
        }
        setSearchParams(nextParams, { replace: true });
        if (!targetId) onClose?.();
      }
    },
    [
      selectedQueueCode,
      assignment,
      workflowStatuses,
      nextId,
      prevId,
      searchParams,
      setSearchParams,
      onClose,
    ]
  );

  // Status update mutation
  const statusUpdateMutation = useMutation({
    mutationFn: (data: PatchedAssignmentStatusUpdate) =>
      AssignmentsService.assignmentsAssignmentStatusUpdatePartialUpdate({
        id: assignmentId,
        requestBody: data,
      }),
    onSuccess: (updated) => {
      toast.success('Status updated');
      invalidateQueries();
      checkAndNavigateIfRemoved(updated as AssignmentDetail);
    },
    onError: (error) => handleApiError(error),
  });

  const handleStatusUpdate = useCallback(
    (data: { statusCode: string | null; reasonCode: string | null; comment?: string }) => {
      statusUpdateMutation.mutate({
        code: data.statusCode || undefined,
        reason_code: data.reasonCode,
        comment: data.comment,
      });
      setIsStatusModalOpen(false);
    },
    [statusUpdateMutation]
  );

  const handleStatusClick = useCallback((status: Status) => {
    setTargetStatusCode(status.code);
    setIsStatusModalOpen(true);
  }, []);

  // Tab setup
  const tabs = useMemo(
    () => [
      { code: 'shifts', label: 'Shifts' },
      {
        code: 'expenses',
        label: expenseCount > 0 ? `Expenses (${expenseCount})` : 'Expenses',
      },
      {
        code: 'documents',
        label: documentCount > 0 ? `Documents (${documentCount})` : 'Documents',
      },
    ],
    [expenseCount, documentCount]
  );

  const { ref: tabsMeasureRef, collapsed: isOverflowCollapsed } =
    useCollapseOnOverflow<HTMLElement>({
      observeChildren: true,
      forceCollapse: isMobile,
      constraintRef: drawerRef,
      recheckOn: { id: assignmentId, tabs },
    });

  // 30-day lockout (exempt for initial response or unconfirmed assignments)
  const currentStatusCode = assignment?.current_status?.code ?? undefined;
  const isNewAssignment = currentStatusCode === StatusCodes.NEW;
  const needsProviderReview = currentStatusCode === StatusCodes.PROV_REV;
  const isLockedOut = useMemo(() => {
    if (isNewAssignment) return false;
    if (!assignment?.start_on) return false;
    if (assignment.current_status?.code !== StatusCodes.CONFIRMED) return false;
    const startDate = parseISO(assignment.start_on);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return differenceInCalendarDays(startDate, today) <= 30;
  }, [assignment?.start_on, isNewAssignment]);

  // Loading / error states
  if (isLoadingAssignment) {
    return <div className="tw:p-4 tw:text-center tw:text-zinc-500">Loading assignment...</div>;
  }

  if (assignmentError || !assignment) {
    return (
      <div className="tw:p-4">
        <div className="tw:flex tw:justify-end">
          <DrawerCloseButton onClick={() => onClose?.()} />
        </div>
        <div className="tw:text-center tw:text-stoplight-danger tw:bg-stoplight-danger/10 tw:my-2 tw:rounded-lg tw:py-4">
          Failed to load assignment.
        </div>
      </div>
    );
  }

  const hideDecline = isNewAssignment || hasShiftDirty || isLockedOut || needsProviderReview;
  const excludedStatusMap: Partial<Record<StatusCodes, boolean>> = {
    [StatusCodes.DECLINED]: hideDecline,
    [StatusCodes.WITHDRAWN]: hideDecline,
  };
  const shift_count =
    assignment.active_shift_count > 0
      ? assignment.active_shift_count
      : assignment.removed_shift_count || 0;
  const LABEL_STYLE: string = READ_ONLY_LABEL_STYLE;
  const VALUE_STYLE: string = READ_ONLY_VALUE_STYLE;
  return (
    <div ref={drawerRef} className="tw:flex tw:flex-col tw:mt-4">
      {/* Header */}
      <div className="tw:border-b tw:border-zinc-200 tw:pb-2">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
          <div className="tw:flex-1 tw:min-w-0">
            <div className="tw:text-lg tw:font-semibold tw:leading-tight">
              {assignment.facility.name}
            </div>
            <div className="tw:text-xs tw:text-zinc-500 tw:font-mono tw:flex tw:items-center tw:gap-1">
              {assignment.code}
              <TooltippedText
                tooltipContent="Copy code"
                tooltipClassName="tw:bg-brand-green-2 tw:text-zinc-700"
                placement="bottom"
              >
                <button
                  type="button"
                  className="tw:text-zinc-600 tw:hover:text-zinc-600 tw:bg-transparent tw:border-none tw:p-0 tw:cursor-pointer"
                  onClick={() => {
                    navigator.clipboard.writeText(assignment.code || '');
                    toast.success('Copied', { autoClose: 1500 });
                  }}
                >
                  <i className="far fa-copy tw:text-[10px]" />
                </button>
              </TooltippedText>
            </div>
          </div>

          {!isFullPage && !isMobile && (
            <button
              type="button"
              className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:p-1"
              onClick={() => {
                const tabParam = activeTab !== defaultTab ? `?tab=${activeTab}` : '';
                navigate(`/app/provider/assignments/${assignmentId}${tabParam}`, {
                  state: { referrer: window.location.pathname + window.location.search },
                });
              }}
              title="Open full page"
            >
              <i className="fas fa-up-right-from-square" />
            </button>
          )}
          <DrawerCloseButton onClick={() => onClose?.()} />
        </div>

        {/* Facility + location */}
        <div className="tw:mt-2 tw:text-sm tw:text-zinc-600 tw:flex tw:flex-wrap tw:gap-x-4">
          <span>
            <i className="fas fa-map-marker-alt tw:text-zinc-400 tw:mr-1" />
            {getFacilityLocationDisplayValue(assignment.facility)}
          </span>
          <span>
            <i className="far fa-calendar tw:text-zinc-400 tw:mr-1" />
            {formatApiDate(assignment.start_on)} - {formatApiDate(assignment.end_on)}
          </span>
          {assignment.source_type === SourceTypeEnum.ORGANIZATION && (
            <span className={clsx('tw:ml-1', SOURCE_TYPE_ORGANIZATION_BADGE_STYLE)}>1099</span>
          )}
        </div>

        <div className="tw:flex tw:flex-row tw:py-2 tw:items-start tw:gap-2 tw:shrink-0 tw:flex-wrap">
          <StatusPill
            scope={WorkflowScopes.Assignment}
            status={assignment.current_status || 'Unknown'}
          />
          {assignment.current_reason?.title && (
            <div
              className={`tw:text-[10px] tw:mt-0.5 tw:text-right tw:max-w-[160px] tw:break-words ${getStatusTextStyle(assignment.current_status)}`}
            >
              {assignment.current_reason.title}
            </div>
          )}
        </div>
        <div>
          <dl className="tw:grid tw:grid-cols-3 tw:gap-2 tw:text-sm tw:!pt-2 tw:!mt-1 tw:!mb-0 tw:border-t tw:border-zinc-200">
            <div>
              <dt className={LABEL_STYLE}>Total Payment</dt>
              <dd className={VALUE_STYLE}>{formatCurrencyPostfix(assignment.total_amount)}</dd>
            </div>

            <div>
              <dt className={LABEL_STYLE}># Shifts</dt>
              <dd className={VALUE_STYLE}>{shift_count}</dd>
            </div>
            {relatedTimecards.length > 0 && (
              <div>
                <dt className={LABEL_STYLE}>
                  {relatedTimecards.length > 1 ? 'Timecards' : 'Timecard'}
                </dt>
                <dd className={VALUE_STYLE}>
                  {relatedTimecards.map((tc, idx) => (
                    <React.Fragment key={tc.id}>
                      {idx > 0 && ', '}
                      <button
                        type="button"
                        className="tw:text-brand-green-0 tw:underline tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer tw:!text-xs"
                        onClick={() =>
                          navigate(`/app/provider/timecards/${tc.id}`, {
                            state: {
                              referrer: window.location.pathname + window.location.search,
                            },
                          })
                        }
                      >
                        {tc.code}
                      </button>
                    </React.Fragment>
                  ))}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Status action buttons */}
        {!isLockedOut && (
          <div className="tw:flex tw:flex-wrap tw:gap-2">
            <StatusTransitionButtons
              isLoading={isLoadingWorkflow}
              currentStatusCode={currentStatusCode}
              workflowMetadata={workflowStatuses as StatusMetadata}
              onStatusUpdate={(status: Status) =>
                statusUpdateMutation.mutate({ code: status.code })
              }
              onStatusClick={handleStatusClick}
              isPending={statusUpdateMutation.isLoading}
              excludedStatusMap={excludedStatusMap}
              small={isMobile}
              className="tw:mt-3"
            />
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs activeTab={activeTab} onChange={setActiveTab}>
        {!isMobile &&
          (isOverflowCollapsed ? (
            <div className="tw:py-2">
              <div className="tw:text-xs tw:text-zinc-500 tw:mb-1 tw:pl-1">Select View</div>
              <DropdownSelect
                inputId="assignment-drawer-section"
                aria-label="Assignment section"
                allOptions={tabs.map((t) => ({ value: t.code, label: t.label }))}
                value={activeTab}
                isSearchable={false}
                isClearable={false}
                onChange={(opt: any) => {
                  if (opt && !Array.isArray(opt)) setActiveTab(opt.value);
                }}
              />
            </div>
          ) : (
            <div className="tw:border-b tw:border-zinc-200">
              <div
                ref={tabsMeasureRef as React.RefObject<HTMLDivElement>}
                className="tw:flex tw:gap-1 tw:overflow-hidden"
              >
                {tabs.map((tab) => (
                  <TabsButton key={tab.code} tab={tab.code}>
                    {tab.label}
                  </TabsButton>
                ))}
              </div>
            </div>
          ))}

        <div className="tw:flex-1 tw:overflow-y-auto sm:tw:pb-0 tw:pb-16">
          <TabsContent tab="shifts">
            <ShiftsTab
              shifts={assignment.shifts || []}
              assignment={assignment}
              onResponseSuccess={invalidateQueries}
              onDirtyChange={setHasShiftDirty}
              isLockedOut={isLockedOut}
            />
          </TabsContent>

          <TabsContent tab="expenses">
            <ExpensesTab
              assignment={assignment}
              canEdit={true}
              ownerId={assignment.healthcare_entity?.id || ''}
              relatedProviderId={assignment.provider?.id}
            />
          </TabsContent>

          <TabsContent tab="documents">
            <DocumentsTab
              contextType="assignments.assignment"
              contextId={assignmentId}
              ownerId={assignment.healthcare_entity?.id ?? ''}
              relatedProviderId={assignment.provider?.id}
            />
          </TabsContent>
          <div className="tw:mt-6 tw:p-2 tw:bg-brand-green-3 tw:rounded-lg">
            <SectionHeader className="tw:mb-2 tw:p-1 tw:text-brand-green-0" headerText="Activity" />
            <ActivityLog
              enableCommentsFilter={true}
              enableStatusChangesFilter={true}
              enableUpdatesFilter={true}
              enableEventsFilter={true}
              composerPlaceholder="Enter a comment (visible to all parties)..."
              notes={notesData}
              statusHistory={statusHistory as ActivityStatusHistory[] | undefined}
              changeHistory={changeHistory as ActivityHistory[] | undefined}
              eventHistory={eventHistoryData as ActivityEventHistory[] | undefined}
              noteMutations={{
                createNote: async (data) => {
                  await AssignmentsService.assignmentsAssignmentNotesCreate({
                    id: assignmentId,
                    requestBody: {
                      comment: data.comment,
                      assignment: assignmentId,
                    } as AssignmentNoteType,
                  });
                },
                updateNote: async (noteId: string, data: PatchedAssignmentNote) => {
                  return AssignmentsService.assignmentsAssignmentNotePartialUpdate({
                    id: noteId,
                    requestBody: data,
                  });
                },
                deleteNote: async (noteId: string) => {
                  await AssignmentsService.assignmentsAssignmentNoteDestroy({
                    id: noteId,
                  });
                },
                notesQueryKey: [ASSIGNMENT_NOTES_QUERY_KEY, assignmentId],
                extraInvalidateQueryKeys: [[ASSIGNMENT_STATUS_HISTORY_QUERY_KEY, assignmentId]],
              }}
            />
          </div>
        </div>

        {isMobile && <TabsBottomBar tabs={tabs.map((t) => ({ tab: t.code, label: t.label }))} />}
      </Tabs>

      {/* Status transition modal */}
      <StatusTransitionModal
        open={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onConfirm={handleStatusUpdate}
        initialStatusCode={currentStatusCode ?? null}
        targetStatusCode={targetStatusCode}
        workflowScope={WorkflowScopes.Assignment}
        isPending={statusUpdateMutation.isLoading}
        currentReasonCode={assignment.current_reason?.code ?? null}
        currentStatusTitle={assignment.current_status?.title ?? null}
      />
    </div>
  );
};
